import { useCallback, useState } from "react";
import { useSite } from "@/hooks/useSite";
import { mediaUrl } from "@/lib/api";
import { LoadState } from "@/components/ErrorBoundary";
import { Seo, LocalBusinessSchema } from "@/components/Seo";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Hero } from "@/components/site/Hero";
import { CapacityNotice } from "@/components/site/CapacityNotice";
import { Works } from "@/components/site/Works";
import { Positioning } from "@/components/site/Positioning";
import { CognitiveSpaceWidget } from "@/components/site/CognitiveSpaceWidget";
import { Motifs } from "@/components/site/Motifs";
import { CustomWork } from "@/components/site/CustomWork";
import { Process } from "@/components/site/Process";
import { Artist } from "@/components/site/Artist";
import { GoogleReviews } from "@/components/site/GoogleReviews";
import { Faq } from "@/components/site/Faq";
import { InquiryForm } from "@/components/site/InquiryForm";
import { Shop } from "@/components/site/Shop";

export default function Home() {
  const { site, status, error, reload } = useSite();
  const [motif, setMotif] = useState(null);

  const scrollToForm = useCallback(() => {
    document.getElementById("anfrage")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const requestMotif = useCallback(
    (m) => {
      setMotif(m);
      setTimeout(scrollToForm, 60);
    },
    [scrollToForm]
  );

  if (!site) return <LoadState status={status} error={error} onRetry={reload} />;

  const { texts, works, custom_works: customWorks, motifs, faqs, products, process, capacity } = site;
  const heroMediaId = texts.hero_image_media_id || works.find((w) => w.image_media_id)?.image_media_id || "";
  const heroFallback = works.find((w) => w.image)?.image || "";
  const artistMediaId = texts.artist_image_media_id || "";
  const socialImage = mediaUrl(texts.social_preview_media_id || heroMediaId, "lg");

  return (
    <>
      <Seo
        title="ArtPart – Tätowierer René Ghavami | Dystopische Tattoos St. Ruprecht an der Raab"
        description="Privates Tattoostudio in St. Ruprecht an der Raab bei Graz, Weiz und Gleisdorf. Individuelle, dystopische und architektonische Black-and-Grey-Tattoos von René Ghavami. Termine nach Vereinbarung."
        path="/"
        image={socialImage}
      />
      <LocalBusinessSchema />
      <Header instagram={texts.instagram_url} />
      <main>
        <Hero texts={texts} heroMediaId={heroMediaId} heroImage={heroFallback} />
        <CapacityNotice capacity={capacity} />
        <Works texts={texts} works={works} />
        <Positioning texts={texts} />
        <CognitiveSpaceWidget />
        <Motifs texts={texts} motifs={motifs} onRequest={requestMotif} />
        <CustomWork
          texts={texts}
          works={customWorks}
          onRequestCustom={() => {
            setMotif(null);
            scrollToForm();
          }}
        />
        <Process texts={texts} steps={process} />
        <Artist texts={texts} mediaId={artistMediaId} />
        <GoogleReviews />
        <InquiryForm
          texts={texts}
          selectedMotif={motif}
          onClearMotif={() => setMotif(null)}
          instagram={texts.instagram_url}
        />
        <Faq texts={texts} faqs={faqs} />
        <Shop texts={texts} products={products} />
      </main>
      <Footer texts={texts} />
    </>
  );
}
